import { useMemo } from 'react'

import { useQuote } from 'components/quote_explorer/QuoteProvider'
import { useQuotingEmployeeContext } from 'components/quoting/employees/QuotingEmployeeContext'
import { CollapsibleContainer, SolidAlertCircleIcon } from 'slab'
import { fullStateNameFromAbbreviation } from 'utils'

export const PreliminaryEstimateCard = () => {
  const { quote } = useQuote()
  const { employees } = useQuotingEmployeeContext()

  const projectedStateEmployees = useMemo(() => {
    if (!quote || !quote.projected_data_state_codes) return []

    return employees.filter(employee => {
      const stateCode = employee.rating_area_code.substring(0, 2)
      return quote.projected_data_state_codes?.includes(stateCode)
    })
  }, [quote?.projected_data_state_codes, employees])

  const projectedStates = useMemo(() => {
    if (!quote || !quote.projected_data_state_codes) return []

    return quote.projected_data_state_codes.map(code => ({
      code,
      name: fullStateNameFromAbbreviation(code),
      employeeCount: employees.filter(emp => emp.rating_area_code.substring(0, 2) === code).length
    })).filter(state => state.employeeCount > 0)
  }, [quote?.projected_data_state_codes, employees])

  if (!quote || !quote.preliminary || projectedStateEmployees.length === 0) {
    return null
  }

  return (
    <div className='bg-white border border-gray-200 rounded-2xl p-4 shadow-md'>
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-xs text-gray-500'>Preliminary plan rates</h2>
        <div className='text-amber-500'>
          <SolidAlertCircleIcon size={16} />
        </div>
      </div>

      <p className='text-xs text-gray-700 mb-4'>
        You have employees in states that have not filed finalized rates for 2026.
        Thatch analyzes preliminary rate filings to provide a cost estimate for these states. Costs are subject to change once finalized rates are available.
      </p>

      <CollapsibleContainer
        heading='View states with preliminary rates'
        defaultOpen={false}
      >
        <div className='text-xs text-gray-700 p-2'>
          <ul className='space-y-1'>
            {projectedStates.map(state => (
              <li key={state.code} className='flex justify-between'>
                <span>{state.name}</span>
                <span className='text-gray-600 font-medium'>
                  {state.employeeCount} employee{state.employeeCount === 1 ? '' : 's'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </CollapsibleContainer>
    </div>
  )
}

export default PreliminaryEstimateCard
